export const normalizeText = (value: string): string => {
  return (value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    // Uwaga: "ł" nie rozkłada się na bazową literę + znak diakrytyczny w NFD,
    // więc musimy mapować je ręcznie, inaczej porównania typu "lomzy" vs "Łomży" nie zadziałają.
    .toLowerCase()
    .replace(/ł/g, "l")
    .trim();
};

// Map of ASCII characters to Polish diacritics for common Polish letters
const polishDiacriticsMap: Record<string, string> = {
  'a': 'ą',
  'c': 'ć',
  'e': 'ę',
  'l': 'ł',
  'n': 'ń',
  'o': 'ó',
  's': 'ś',
  'z': 'ż', // Also maps to ź but ż is more common
};

// Generate all possible Polish diacritic variants of a word
// e.g., "sloneczna" -> ["sloneczna", "słoneczna", "slonęczna", ...]
// Returns the most likely Polish variant based on common patterns
export const addPolishDiacritics = (text: string): string => {
  if (!text) return text;
  
  // Common Polish word patterns - map normalized form to proper Polish
  const commonPatterns: Record<string, string> = {
    'sloneczna': 'Słoneczna',
    'sloneczne': 'Słoneczne', 
    'zolnierska': 'Żołnierska',
    'zolnierzy': 'Żołnierzy',
    'zrodlana': 'Źródlana',
    'zrodlowa': 'Źródłowa',
    'swidnicka': 'Świdnicka',
    'sniezna': 'Śnieżna',
    'gorczanska': 'Gorczańska',
    'laczna': 'Łączna',
    'lakowa': 'Łąkowa',
    'lodowa': 'Lodowa',
    'cicha': 'Cicha',
    'dluga': 'Długa',
    'gorna': 'Górna',
    'dolna': 'Dolna',
    'glowna': 'Główna',
    'polna': 'Polna',
    'lesna': 'Leśna',
    'kwiatowa': 'Kwiatowa',
    'ogrodowa': 'Ogrodowa',
    'parkowa': 'Parkowa',
    'sportowa': 'Sportowa',
    'szkolna': 'Szkolna',
    'koscielna': 'Kościelna',
    'kolejowa': 'Kolejowa',
    'dworcowa': 'Dworcowa',
    'targowa': 'Targowa',
    'rynek': 'Rynek',
    'wolnosci': 'Wolności',
    'niepodleglosci': 'Niepodległości',
    'konstytucji': 'Konstytucji',
    'powstancow': 'Powstańców',
    'zielona': 'Zielona',
    'biala': 'Biała',
    'czerwona': 'Czerwona',
    'rozana': 'Różana',
    'brzozowa': 'Brzozowa',
    'debowa': 'Dębowa',
    'sosnowa': 'Sosnowa',
    'lipowa': 'Lipowa',
    'klonowa': 'Klonowa',
    'topolowa': 'Topolowa',
    'jabloniowa': 'Jabłoniowa',
    'wisniowa': 'Wiśniowa',
    'kasztanowa': 'Kasztanowa',
    'akacjowa': 'Akacjowa',
    'jasminowa': 'Jaśminowa',
    'rozowa': 'Różowa',
    'modrzewiowa': 'Modrzewiowa',
    'swierczewskiego': 'Świerczewskiego',
    'mickiewicza': 'Mickiewicza',
    'slowackiego': 'Słowackiego',
    'sienkiewicza': 'Sienkiewicza',
    'zeromskiego': 'Żeromskiego',
    'orzeszkowej': 'Orzeszkowej',
    'konopnickiej': 'Konopnickiej',
    'kopernika': 'Kopernika',
    'pilsudskiego': 'Piłsudskiego',
    'kosciuszki': 'Kościuszki',
    'poniatowskiego': 'Poniatowskiego',
    'dabrowskiego': 'Dąbrowskiego',
    'krolowej': 'Królowej',
    'jadwigi': 'Jadwigi',
    'jana': 'Jana',
    'pawla': 'Pawła',
    'swietego': 'Świętego',
    'sw': 'Św',
  };

  const normalized = normalizeText(text);
  
  // Check if we have a known pattern
  if (commonPatterns[normalized]) {
    return commonPatterns[normalized];
  }
  
  // For unknown words, try basic letter substitutions for the most common patterns
  let result = text;
  
  // Apply common substitutions if the text looks like it needs Polish characters
  // This is a heuristic approach for words not in our dictionary
  const substitutions: [RegExp, string][] = [
    [/\bsl(?=[aeiou])/gi, 'Sł'],  // sl + vowel -> Sł (like Słoneczna)
    [/\bz(?=[aeiou])/gi, 'Ż'],   // z + vowel at start -> Ż (like Żołnierska)
    [/\bs(?=[wn])/gi, 'Ś'],      // s before w or n -> Ś (like Świdnicka, Śnieżna)
    [/n(?=sk)/gi, 'ń'],          // n before sk -> ń (like Gorczańska)
    [/l(?=sk)/gi, 'ł'],          // l before sk -> ł (like Krakowska stays but Łącka)
  ];
  
  // Only apply if text is all ASCII (no Polish chars already)
  if (!/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/.test(text)) {
    // Don't modify, just return original - let Nominatim handle it
    // The issue is Nominatim needs Polish chars, so we search with both variants
  }
  
  return text;
};

// Create search variants with and without Polish diacritics
export const getSearchVariants = (text: string): string[] => {
  if (!text) return [text];
  
  const normalized = normalizeText(text);
  const variants = new Set<string>([text]);
  
  // Common Polish word patterns - map normalized form to proper Polish
  const commonPatterns: Record<string, string> = {
    'sloneczna': 'Słoneczna',
    'sloneczne': 'Słoneczne',
    'sloneczny': 'Słoneczny',
    'zolnierska': 'Żołnierska',
    'swidnicka': 'Świdnicka',
    'sniezna': 'Śnieżna',
    'zrodlana': 'Źródlana',
    'zrodlowa': 'Źródłowa',
    'laczna': 'Łączna',
    'lakowa': 'Łąkowa',
    'dluga': 'Długa',
    'gorna': 'Górna',
    'dolna': 'Dolna',
    'glowna': 'Główna',
    'lesna': 'Leśna',
    'koscielna': 'Kościelna',
    'wolnosci': 'Wolności',
    'niepodleglosci': 'Niepodległości',
    'powstancow': 'Powstańców',
    'zielona': 'Zielona',
    'biala': 'Biała',
    'rozana': 'Różana',
    'debowa': 'Dębowa',
    'jabloniowa': 'Jabłoniowa',
    'wisniowa': 'Wiśniowa',
    'jasminowa': 'Jaśminowa',
    'rozowa': 'Różowa',
    'swierczewskiego': 'Świerczewskiego',
    'slowackiego': 'Słowackiego',
    'zeromskiego': 'Żeromskiego',
    'pilsudskiego': 'Piłsudskiego',
    'kosciuszki': 'Kościuszki',
    'dabrowskiego': 'Dąbrowskiego',
    'krolowej': 'Królowej',
    'pawla': 'Pawła',
    'swietego': 'Świętego',
    'lomza': 'Łomża',
    'lomzy': 'Łomży',
    'lodz': 'Łódź',
    'lodzi': 'Łodzi',
    'gdansk': 'Gdańsk',
    'gdanska': 'Gdańska',
    'poznan': 'Poznań',
    'poznania': 'Poznania',
    'wroclaw': 'Wrocław',
    'wroclawia': 'Wrocławia',
    'krakow': 'Kraków',
    'krakowa': 'Krakowa',
    'bialystok': 'Białystok',
    'bialystoku': 'Białegostoku',
    'czestochowa': 'Częstochowa',
    'czestochowy': 'Częstochowy',
    'swinoujscie': 'Świnoujście',
    'kolobrzeg': 'Kołobrzeg',
    'miedzyzdroje': 'Międzyzdroje',
  };

  // Check if we have a known pattern for whole word or partial match
  for (const [key, value] of Object.entries(commonPatterns)) {
    if (normalized === key || normalized.includes(key)) {
      variants.add(value);
      variants.add(text.replace(new RegExp(key, 'gi'), value));
    }
  }
  
  return Array.from(variants);
};

export const includesNormalized = (haystack: string, needle: string): boolean => {
  const h = normalizeText(haystack);
  const n = normalizeText(needle);
  if (!n) return false;
  return h.includes(n);
};

export const startsWithNormalized = (value: string, prefix: string): boolean => {
  const v = normalizeText(value);
  const p = normalizeText(prefix);
  if (!p) return false;
  return v.startsWith(p);
};

export const stripRoadPrefix = (road: string): string => {
  // Nominatim potrafi zwracać prefiksy typu: "ulica", "ul.", "aleja" itd.
  return (road || "")
    .trim()
    .replace(/^ulica\s+/i, "")
    .replace(/^ul\.\s*/i, "")
    .replace(/^aleja\s+/i, "")
    .replace(/^al\.\s*/i, "")
    .replace(/^plac\s+/i, "")
    .replace(/^osiedle\s+/i, "")
    .trim();
};

export const extractRoadFromDisplayName = (displayName: string): string => {
  const firstPart = (displayName || "").split(",")[0]?.trim() ?? "";
  return stripRoadPrefix(firstPart);
};

// Mapa nazw województw z Nominatim na polskie nazwy z małej litery
export const normalizeRegion = (state: string | undefined): string => {
  if (!state) return "";

  // Nominatim zwraca np. "województwo mazowieckie" - wyciągamy nazwę
  const match = state.match(/województwo\s+(.+)/i);
  if (match) {
    return match[1].toLowerCase();
  }

  // Jeśli format jest inny, zwróć małymi literami
  return state.toLowerCase().replace("województwo", "").trim();
};

// Formatowanie nazwy miasta/adresu z wielkich liter
export const capitalizeWords = (str: string): string => {
  const parts = (str || "").split(" ");
  return parts
    .map((word, idx) => {
      if (word.length === 0) return word;
      // Małe słowa (spójniki itp.) zostawiamy małe, chyba że są na początku
      const smallWords = ["i", "w", "na", "do", "z", "od", "po", "przy", "za", "dla"];
      if (idx > 0 && smallWords.includes(word.toLowerCase())) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};
